import React from 'react';
import { Calendar, Layers } from 'lucide-react';

interface YearSelectorProps {
  availableYears: string[];
  selectedYear: string;
  onYearChange: (year: string) => void;
  className?: string;
}

export const YearSelector: React.FC<YearSelectorProps> = ({
  availableYears,
  selectedYear,
  onYearChange,
  className = '',
}) => {
  const options = [...availableYears, 'all'];

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Label for context */}
      <span className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider">
        <Calendar className="w-3.5 h-3.5 text-emerald-700" />
        <span>Year:</span>
      </span>

      {/* Desktop Segmented Pill Group */}
      <div className="hidden sm:flex items-center gap-1 p-1 bg-slate-200/80 rounded-xl shadow-inner border border-slate-300/60">
        {options.map((y) => {
          const isSelected = selectedYear === y;
          const label = y === 'all' ? 'All Years' : y;
          return (
            <button
              key={y}
              onClick={() => onYearChange(y)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                isSelected
                  ? 'bg-emerald-800 text-white shadow-sm ring-1 ring-emerald-900/10'
                  : 'text-slate-700 hover:text-slate-900 hover:bg-slate-300/50'
              }`}
            >
              {y === 'all' && <Layers className="w-3 h-3" />}
              <span>{label}</span>
            </button>
          );
        })}
      </div>

      {/* Mobile Compact Dropdown */}
      <div className="sm:hidden flex items-center gap-1.5">
        <label htmlFor="mobile-year-select" className="text-xs font-bold text-slate-700 flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5 text-emerald-700" />
          <span>Year:</span>
        </label>
        <select
          id="mobile-year-select"
          value={selectedYear}
          onChange={(e) => onYearChange(e.target.value)}
          aria-label="Select Reporting Year"
          className="h-10 min-h-[44px] px-2.5 text-xs font-bold bg-white border border-slate-300 rounded-lg text-slate-850 focus:outline-none focus:ring-2 focus:ring-emerald-600 shadow-xs"
        >
          {options.map((y) => (
            <option key={y} value={y}>
              {y === 'all' ? 'All Years (Multi-Year)' : y}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};
